/**
 * E018-S011-T002: WorkspaceAutomationsPanel
 *
 * Sidebar panel showing all automations across the workspace.
 * Replaces AutomationsOverview with workspace-wide, reactive architecture.
 *
 * Features:
 * - Workspace-wide via AutomationDiscoveryService
 * - Reactive Streams (no polling)
 * - Grouped by Doc with current doc highlighted
 * - Search/filter functionality
 * - Canvas-only (In-Note filtered out)
 * - Cinematic navigation for current doc
 * - Navigate to other docs
 * - Configure button (opens sidebar)
 * - Delete button (current doc only)
 */
import { Tooltip } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import { createStreamLainFlowBlockAtCenter } from '@blocksuite/affine/gfx/mindmap';
import { deleteBlocksThroughGate } from '@blocksuite/affine-block-surface';
import { parseXYWH, serializeXYWH } from '@blocksuite/global/gfx';
import {
  ArrowRightBigIcon,
  DeleteIcon,
  MoveToIcon,
  OpenInNewIcon,
  PageIcon,
  PlayIcon,
  SettingsIcon,
} from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import clsx from 'clsx';
import {
  cloneElement,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DisclosureChevron } from '../../../components/animation/disclosure-chevron';
import {
  SidebarExpandableGroup,
  SidebarExpandableSection,
} from '../../../components/sidebar-expandable-section';
import { AnimatedNavigationPanelTreeChildren } from '../../../desktop/components/navigation-panel/tree/animated-children';
import { DocService, DocsService } from '../../doc';
import { EditorService } from '../../editor';
import { AppThemeService } from '../../theme';
import { WorkbenchService } from '../../workbench/services/workbench';
import { WorkspaceService } from '../../workspace';
import { useAutomationDocSummaries } from '../hooks/use-automation-doc-summaries';
import {
  filterDocGroupsForHiddenMailFolderDocIds,
  useHiddenMailFolderDocIds,
} from '../mail/mail-folder-visibility-filter';
import { AutomationService } from '../services/automation';
import {
  AutomationDiscoveryService,
  type AutomationInfo,
} from '../services/automation-discovery';
import {
  OPEN_WORKFLOW_AUTOMATION_USAGE_EVENT,
  type OpenWorkflowAutomationUsageDetail,
} from '../services/automation-sidebar-sync';
import { ReviewService } from '../services/review';
import { WorkflowService } from '../services/workflow';
import { cinematicNavigateTo } from '../utils/cinematic-viewport';
import { whenEdgelessRoot } from '../utils/when-edgeless-root';
import {
  AutomationOverviewStatusLine,
  resolveAutomationOverviewStatus,
} from './automation-overview-status';
import { withMovedCanvasBlockXYWH } from './canvas-block-move';
import { ReviewPanel } from './components/review-panel';
import { TRIGGER_ICONS } from './trigger-icons';
import * as automationStyles from './workspace-automations-panel.css';
import { WorkspaceOperationsChip } from './workspace-operations-chip';
import * as styles from './workspace-overview.css';
import { WorkspaceOverviewSearch } from './workspace-overview-search';

// =============================================================================
// Types
// =============================================================================

interface DocGroup {
  docId: string;
  docTitle: string;
  automations: AutomationInfo[];
  isCurrent: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function WorkspaceAutomationsPanel() {
  const t = useI18n();
  const docService = useServiceOptional(DocService);
  const docsService = useService(DocsService);
  const editorService = useServiceOptional(EditorService);
  const workbenchService = useService(WorkbenchService);
  const workspaceService = useService(WorkspaceService);
  const discoveryService = useService(AutomationDiscoveryService);
  const automationService = useService(AutomationService);
  const reviewService = useService(ReviewService);
  const workflowService = useService(WorkflowService);

  const pendingCount =
    useLiveData(reviewService.activityStats$)?.pendingCount ?? 0;
  const [pendingExpanded, setPendingExpanded] = useState(false);

  // sl-djze.3: Listen for pending-decision-review events → expand pending section
  useEffect(() => {
    const handler = () => setPendingExpanded(true);
    window.addEventListener('pending-decision-review', handler);
    return () => window.removeEventListener('pending-decision-review', handler);
  }, []);

  // sl-67kc.3.4: Deep-link from the workspace operations chip. The chip
  // lives in this panel header; clicks on a flyout row fire this event
  // with the target doc + block + log. The handler must:
  //   1. Navigate the workbench to the target doc so the automation
  //      sidebar loads.
  //   2. Activate the target block so the block-scoped HistoryPanel
  //      actually mounts (otherwise the deep-link silently dead-ends
  //      whenever the chip points at a different automation than the
  //      one currently active — see sl-67kc.3.4 F1).
  //   3. Publish the target row on AutomationService. HistoryPanel reads
  //      the LiveData on mount and scrolls itself into view, which wins
  //      the first-mount race that CustomEvent scroll dispatch could not.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | {
            docId: string;
            blockId: string;
            executionLogId: string;
            suspendedExecutionId: string;
          }
        | undefined;
      if (!detail?.docId) return;
      workbenchService.workbench.open(`/${detail.docId}`);
      automationService.setActiveBlock(detail.blockId);
      automationService.setPendingHistoryRowTarget({
        blockId: detail.blockId,
        executionLogId: detail.executionLogId,
      });
    };
    window.addEventListener('streamlain:open-automation-history', handler);
    return () =>
      window.removeEventListener('streamlain:open-automation-history', handler);
  }, [workbenchService, automationService]);

  // Use canvas-only automations (In-Note filtered out)
  const automationsByDoc = useLiveData(
    discoveryService.canvasAutomationsByDoc$
  );
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();
  const visibleAutomationsByDoc = useMemo(
    () =>
      filterDocGroupsForHiddenMailFolderDocIds(
        automationsByDoc,
        hiddenMailFolderDocIds
      ),
    [automationsByDoc, hiddenMailFolderDocIds]
  );
  const currentDocId = docService?.doc.id;
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);
  const [isMoving, setIsMoving] = useState(false);
  const appTheme = useLiveData(useService(AppThemeService).appTheme.theme$);

  // The first automation: it lands in the middle of the document's canvas, where automations live.
  const creation = useRef<AbortController | null>(null);
  useEffect(() => () => creation.current?.abort(), []);
  const handleCreateFirst = useCallback(() => {
    if (!editor) return;
    creation.current?.abort();
    const pending = (creation.current = new AbortController());
    const onCanvas =
      editor.mode$.value === 'edgeless' &&
      !editor.viewMode$.value &&
      !editor.fullscreenWorkflow$.value;
    if (!onCanvas) editor.setMode('edgeless');
    whenEdgelessRoot(pending.signal)
      .then(root =>
        createStreamLainFlowBlockAtCenter('automation', root, 'toolbar:general')
      )
      .catch(error => {
        if (!pending.signal.aborted) {
          console.error('[WorkspaceAutomations] Failed to create:', error);
        }
      });
  }, [editor]);

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [pendingUsageTarget, setPendingUsageTarget] =
    useState<OpenWorkflowAutomationUsageDetail | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (
        e as CustomEvent<OpenWorkflowAutomationUsageDetail | undefined>
      ).detail;
      if (!detail?._routed) return;
      setPendingUsageTarget(detail);
    };

    window.addEventListener(OPEN_WORKFLOW_AUTOMATION_USAGE_EVENT, handler);
    return () =>
      window.removeEventListener(OPEN_WORKFLOW_AUTOMATION_USAGE_EVENT, handler);
  }, []);

  // Move active automation to trashcan (current doc only)
  const handleMoveToTrash = useCallback(
    async (automation: AutomationInfo) => {
      const doc = editorContainer?.doc;
      if (!doc) return;

      try {
        const std = editorContainer.std;
        const model = doc.getBlock(automation.blockId)?.model;
        if (!model) return;
        const [outcome] = await deleteBlocksThroughGate(std, [model]);
        if (outcome === 'deleted') {
          discoveryService.refresh();
        }
      } catch (e) {
        console.error('[WorkspaceAutomations] Failed to move to trash:', e);
      }
    },
    [editorContainer, discoveryService]
  );

  // Configure automation - open in sidebar
  const handleConfigure = useCallback(
    (automation: AutomationInfo) => {
      automationService.setActiveBlock(automation.blockId);
    },
    [automationService]
  );

  // E018-S011-T003: Move automation to current doc
  const handleMove = useCallback(
    async (automation: AutomationInfo) => {
      if (!currentDocId || automation.docId === currentDocId || isMoving)
        return;

      const targetDoc = editorContainer?.doc;
      if (!targetDoc) {
        console.error('[WorkspaceAutomations] No target doc available');
        return;
      }

      // Get gfx controller for viewport positioning
      const edgelessRoot = document.querySelector('affine-edgeless-root');
      const gfx = (
        edgelessRoot as {
          gfx?: { viewport?: { centerX: number; centerY: number } };
        } | null
      )?.gfx;
      if (!gfx?.viewport) {
        console.error(
          '[WorkspaceAutomations] No gfx/viewport - are you in Edgeless mode?'
        );
        return;
      }

      setIsMoving(true);
      console.log('[WorkspaceAutomations] Starting move:', {
        from: automation.docId,
        to: currentDocId,
        blockId: automation.blockId,
      });

      try {
        // Open source doc
        const { doc: sourceDocRecord, release: releaseSource } =
          docsService.open(automation.docId);
        const sourceBlockSuiteDoc = sourceDocRecord.blockSuiteDoc;

        if (!sourceBlockSuiteDoc) {
          console.error(
            '[WorkspaceAutomations] Source doc has no blockSuiteDoc'
          );
          releaseSource();
          return;
        }

        // Find the automation block in source
        const automationBlock = sourceBlockSuiteDoc.getBlock(
          automation.blockId
        );
        if (!automationBlock) {
          console.error('[WorkspaceAutomations] Automation block not found');
          releaseSource();
          return;
        }

        // Get source xywh for size
        const sourceProps = automationBlock.model.props as { xywh?: string };
        const sourceXywh = sourceProps.xywh;
        let width = 300;
        let height = 200;
        if (sourceXywh) {
          try {
            const [, , w, h] = JSON.parse(sourceXywh);
            width = w;
            height = h;
          } catch {
            console.warn('[WorkspaceAutomations] Could not parse source xywh');
          }
        }

        // Calculate position at viewport center
        const centerX = gfx.viewport.centerX;
        const centerY = gfx.viewport.centerY;
        const newXywh = serializeXYWH(
          centerX - width / 2,
          centerY - height / 2,
          width,
          height
        );

        // Use BlockSuite Transformer for proper insertion
        const transformer = targetDoc.getTransformer();
        const blockSnapshot = transformer.blockToSnapshot(
          automationBlock.model
        );
        if (!blockSnapshot) {
          console.error(
            '[WorkspaceAutomations] Failed to create BlockSnapshot'
          );
          releaseSource();
          return;
        }

        // Insert using transformer
        const movedBlockSnapshot = withMovedCanvasBlockXYWH(
          blockSnapshot,
          newXywh
        );
        const restoredModel = await transformer.snapshotToBlock(
          movedBlockSnapshot,
          targetDoc,
          targetDoc.root?.id
        );

        if (!restoredModel) {
          console.error('[WorkspaceAutomations] snapshotToBlock failed');
          releaseSource();
          return;
        }

        const newBlockId = restoredModel.id;
        console.log('[WorkspaceAutomations] Block inserted:', newBlockId);

        // Update position to viewport center AND fix doc references
        // If sourceDocId/targetDocId were null (same-doc), they need to point to the old doc
        const oldDocId = automation.docId;
        const updateProps: Record<string, unknown> = { xywh: newXywh };

        // E018-S014: Debug logging for Cross-Doc Move
        console.log('[WorkspaceAutomations] Move - automation info:', {
          sourceTableId: automation.sourceTableId,
          sourceDocId: automation.sourceDocId,
          targetTableId: automation.targetTableId,
          targetDocId: automation.targetDocId,
          oldDocId,
        });

        // Fix source database reference - if it was same-doc, now it's cross-doc
        if (automation.sourceTableId && !automation.sourceDocId) {
          updateProps.sourceDocId = oldDocId;
          console.log('[WorkspaceAutomations] Setting sourceDocId:', oldDocId);
        }

        // Fix target database reference - if it was same-doc, now it's cross-doc
        if (automation.targetTableId && !automation.targetDocId) {
          updateProps.targetDocId = oldDocId;
          console.log('[WorkspaceAutomations] Setting targetDocId:', oldDocId);
        }

        console.log('[WorkspaceAutomations] updateProps:', updateProps);
        targetDoc.updateBlock(newBlockId, updateProps);

        // Delete from source
        sourceBlockSuiteDoc.deleteBlock(automationBlock.model);
        console.log('[WorkspaceAutomations] Deleted from source');

        releaseSource();

        // Refresh discovery cache
        discoveryService.refresh();

        // Wait for discovery to detect the moved automation
        await new Promise<void>(resolve => {
          let resolved = false;
          let subscription: { unsubscribe: () => void } | null = null;

          const cleanup = () => {
            if (subscription) {
              subscription.unsubscribe();
            }
          };

          subscription = discoveryService.automations$.subscribe(
            automations => {
              if (
                automations.some(a => a.blockId === newBlockId) &&
                !resolved
              ) {
                resolved = true;
                setTimeout(() => {
                  cleanup();
                  resolve();
                }, 0);
              }
            }
          );

          // Fallback: resolve after 1s
          setTimeout(() => {
            if (!resolved) {
              resolved = true;
              cleanup();
              console.warn(
                '[WorkspaceAutomations] Discovery timeout - proceeding anyway'
              );
              resolve();
            }
          }, 1000);
        });

        // Dispatch event for edge/ghost rescan
        window.dispatchEvent(
          new CustomEvent('streamlain:automation-moved', {
            detail: {
              automationId: newBlockId,
              fromDocId: automation.docId,
              toDocId: currentDocId,
            },
          })
        );

        console.log('[WorkspaceAutomations] Move successful:', {
          blockId: newBlockId,
          position: newXywh,
        });
      } catch (e) {
        console.error('[WorkspaceAutomations] Move failed:', e);
      } finally {
        setIsMoving(false);
      }
    },
    [currentDocId, editorContainer, discoveryService, docsService, isMoving]
  );

  // Group automations by doc with current doc first
  const docGroups = useMemo((): DocGroup[] => {
    const groups: DocGroup[] = visibleAutomationsByDoc.map(group => ({
      docId: group.docId,
      docTitle: group.docTitle,
      automations: group.automations,
      isCurrent: group.docId === currentDocId,
    }));

    // Filter by search query
    const filtered = searchQuery.trim()
      ? groups
          .map(group => ({
            ...group,
            automations: group.automations.filter(
              a =>
                a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                group.docTitle.toLowerCase().includes(searchQuery.toLowerCase())
            ),
          }))
          .filter(group => group.automations.length > 0)
      : groups;

    // Sort: current doc first, then alphabetically
    return filtered.sort((a, b) => {
      if (a.isCurrent && !b.isCurrent) return -1;
      if (!a.isCurrent && b.isCurrent) return 1;
      return a.docTitle.localeCompare(b.docTitle);
    });
  }, [visibleAutomationsByDoc, currentDocId, searchQuery]);

  useEffect(() => {
    if (!pendingUsageTarget || pendingUsageTarget.docId !== currentDocId) {
      return;
    }

    const targetInActiveList = docGroups.some(
      group =>
        group.docId === pendingUsageTarget.docId &&
        group.automations.some(
          automation => automation.blockId === pendingUsageTarget.blockId
        )
    );
    if (targetInActiveList) {
      setExpandedDocs(prev => {
        const next = new Set(prev);
        next.delete(`collapsed:${pendingUsageTarget.docId}`);
        next.add(pendingUsageTarget.docId);
        return next;
      });
    }

    if (targetInActiveList) {
      setPendingUsageTarget(null);
    }
  }, [currentDocId, docGroups, pendingUsageTarget]);

  // Toggle doc section expansion
  const toggleDocExpanded = useCallback((docId: string, isCurrent: boolean) => {
    setExpandedDocs(prev => {
      const next = new Set(prev);
      if (isCurrent) {
        const collapsedKey = `collapsed:${docId}`;
        if (next.has(collapsedKey)) {
          next.delete(collapsedKey);
        } else {
          next.delete(docId);
          next.add(collapsedKey);
        }
        return next;
      }
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  }, []);

  // Check if doc is expanded (current doc expanded by default)
  const isExpanded = useCallback(
    (docId: string, isCurrent: boolean) => {
      if (expandedDocs.has(docId)) return true;
      return isCurrent && !expandedDocs.has(`collapsed:${docId}`);
    },
    [expandedDocs]
  );

  // Trigger, schedule state and last run for the expanded doc groups
  const summaryRequests = useMemo(
    () =>
      visibleAutomationsByDoc
        .filter(group => isExpanded(group.docId, group.docId === currentDocId))
        .map(group => ({
          docId: group.docId,
          blockIds: group.automations.map(automation => automation.blockId),
        })),
    [visibleAutomationsByDoc, isExpanded, currentDocId]
  );
  const summariesByDoc = useAutomationDocSummaries(
    workspaceService.workspace.id,
    summaryRequests
  );

  // Navigate to automation in other doc
  const handleNavigate = useCallback(
    (automation: AutomationInfo) => {
      workbenchService.workbench.open(`/${automation.docId}`);
    },
    [workbenchService]
  );

  // Cinematic navigation for current doc automations
  const handleCinematicNavigate = useCallback((automation: AutomationInfo) => {
    const parsedXYWH = parseXYWH(automation.xywh);
    if (!parsedXYWH) return;

    try {
      const [x, y, w, h] = parsedXYWH;
      const centerX = x + w / 2;
      const centerY = y + h / 2;

      const edgelessRoot = document.querySelector('affine-edgeless-root');
      if (edgelessRoot) {
        const gfx = (edgelessRoot as { gfx?: { viewport?: unknown } })?.gfx;
        if (gfx?.viewport) {
          cinematicNavigateTo(
            gfx.viewport as {
              centerX: number;
              centerY: number;
              zoom: number;
              setCenter: (x: number, y: number) => void;
              setZoom: (
                zoom: number,
                focusPoint?: { x: number; y: number }
              ) => void;
            },
            centerX,
            centerY,
            { duration: 700, targetZoom: 1.0 }
          ).catch(console.error);
        }
      }
    } catch (e) {
      console.error('[WorkspaceAutomations] Failed to navigate:', e);
    }
  }, []);

  const totalAutomations = useMemo(
    () => docGroups.reduce((sum, g) => sum + g.automations.length, 0),
    [docGroups]
  );

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>
          {t['com.streamlain.streamlain.workspace.automations']()}
        </span>
        <div className={styles.headerActions}>
          {/* sl-67kc.3.4: workspace-wide aggregator chip for parked LROs */}
          <WorkspaceOperationsChip />
          <span className={styles.count}>{totalAutomations}</span>
          <Tooltip
            content={t[
              'com.streamlain.streamlain.workspace.open-workflow-builder'
            ]()}
          >
            <button
              className={styles.headerActionButton}
              onClick={() => {
                const workflows = workflowService.workflows$.value;
                const first = workflows[0];
                if (editor) {
                  editor.openWorkflowFullscreen(
                    first?.id ?? '',
                    first?.name ?? ''
                  );
                } else {
                  window.dispatchEvent(
                    new CustomEvent('streamlain:workflow-fullscreen', {
                      detail: {
                        workflowId: first?.id ?? '',
                        workflowName: first?.name ?? '',
                      },
                    })
                  );
                }
              }}
            >
              <OpenInNewIcon width={16} height={16} />
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Search */}
      <WorkspaceOverviewSearch
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={t[
          'com.streamlain.streamlain.workspace.search-automations'
        ]()}
      />

      {/* Scrollable Content */}
      <div className={styles.scrollableContent}>
        {docGroups.length === 0 ? (
          <div className={styles.emptyState} data-testid="automations-empty">
            <span className={styles.emptyArt} aria-hidden="true">
              {createElement('streamlain-flow-art', {
                'app-theme': appTheme === 'dark' ? 'dark' : 'light',
              })}
            </span>
            <span>
              {t['com.streamlain.streamlain.workspace.automations.empty']()}
            </span>
            <span className={styles.emptyHint}>
              {t[
                'com.streamlain.streamlain.workspace.automations.empty-hint'
              ]()}
            </span>
            {editor ? (
              <Button
                variant="primary"
                className={styles.emptyAction}
                data-testid="automations-empty-create"
                onClick={handleCreateFirst}
              >
                {t['Create']()}
              </Button>
            ) : null}
          </div>
        ) : (
          docGroups.map(group => {
            const expanded = isExpanded(group.docId, group.isCurrent);
            const docSummaries = summariesByDoc.get(group.docId);

            return (
              <div key={group.docId} className={styles.docSection}>
                {/* Doc Header */}
                <div
                  className={
                    group.isCurrent ? styles.docHeaderCurrent : styles.docHeader
                  }
                  onClick={() =>
                    toggleDocExpanded(group.docId, group.isCurrent)
                  }
                >
                  <DisclosureChevron
                    expanded={expanded}
                    className={styles.chevron}
                  />
                  <PageIcon width={16} height={16} className={styles.docIcon} />
                  <span className={styles.docTitle}>{group.docTitle}</span>
                  {group.isCurrent && (
                    <span className={styles.currentBadge}>
                      {t['com.streamlain.streamlain.common.current']()}
                    </span>
                  )}
                  <span className={styles.count}>
                    {group.automations.length}
                  </span>
                </div>

                {/* Automation List */}
                <AnimatedNavigationPanelTreeChildren>
                  {expanded ? (
                    <div
                      key={`${group.docId}:automations`}
                      className={styles.list}
                    >
                      {group.automations.map(automation => {
                        const summary = docSummaries?.get(automation.blockId);
                        return (
                          <div
                            key={automation.blockId}
                            className={styles.item}
                            onClick={() => {
                              if (group.isCurrent) {
                                handleCinematicNavigate(automation);
                              } else {
                                handleNavigate(automation);
                              }
                            }}
                          >
                            <div
                              className={clsx(
                                styles.itemIcon,
                                automationStyles.iconTone[
                                  resolveAutomationOverviewStatus(summary)
                                ]
                              )}
                            >
                              {summary ? (
                                cloneElement(
                                  TRIGGER_ICONS[summary.triggerType],
                                  {
                                    width: 16,
                                    height: 16,
                                  }
                                )
                              ) : (
                                <PlayIcon width={16} height={16} />
                              )}
                            </div>
                            <div className={styles.itemInfo}>
                              <div className={styles.itemName}>
                                {automation.name}
                              </div>
                              {docSummaries && (
                                <AutomationOverviewStatusLine
                                  summary={summary}
                                />
                              )}
                            </div>

                            {/* Actions for current doc */}
                            {group.isCurrent && (
                              <div className={styles.itemActions}>
                                <Tooltip
                                  content={t[
                                    'com.streamlain.streamlain.common.configure'
                                  ]()}
                                >
                                  <button
                                    className={styles.itemAction}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleConfigure(automation);
                                    }}
                                  >
                                    <SettingsIcon width={14} height={14} />
                                  </button>
                                </Tooltip>
                                <Tooltip
                                  content={t[
                                    'com.streamlain.streamlain.common.move-to-trash'
                                  ]()}
                                >
                                  <button
                                    className={styles.itemAction}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleMoveToTrash(automation).catch(
                                        console.error
                                      );
                                    }}
                                  >
                                    <DeleteIcon width={14} height={14} />
                                  </button>
                                </Tooltip>
                              </div>
                            )}

                            {/* Actions for other docs: Move + Navigate */}
                            {!group.isCurrent && (
                              <div className={styles.itemActions}>
                                <Tooltip
                                  content={t[
                                    'com.streamlain.streamlain.common.move-to-current-doc'
                                  ]()}
                                >
                                  <button
                                    className={styles.itemAction}
                                    disabled={isMoving}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleMove(automation).catch(
                                        console.error
                                      );
                                    }}
                                  >
                                    <MoveToIcon width={14} height={14} />
                                  </button>
                                </Tooltip>
                                <Tooltip
                                  content={t[
                                    'com.streamlain.streamlain.common.go-to-source'
                                  ]()}
                                >
                                  <button
                                    className={styles.itemActionNeutral}
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleNavigate(automation);
                                    }}
                                  >
                                    <ArrowRightBigIcon width={14} height={14} />
                                  </button>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </AnimatedNavigationPanelTreeChildren>
              </div>
            );
          })
        )}
      </div>

      {pendingCount > 0 && (
        <SidebarExpandableGroup placement="bottom">
          {/* Pending Decisions (collapsible) */}
          {pendingCount > 0 && (
            <SidebarExpandableSection
              expanded={pendingExpanded}
              onExpandedChange={setPendingExpanded}
              placement="bottom"
              title={`${t['com.streamlain.streamlain.review.pending-decisions']()} (${pendingCount})`}
              contentClassName={styles.bottomSectionContent}
            >
              <ReviewPanel hideActivityChart />
            </SidebarExpandableSection>
          )}
        </SidebarExpandableGroup>
      )}
    </div>
  );
}
